import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TrustPaymentsFxQuoteFx, trustPaymentsService } from "src/api";
import { Theme, useTheme } from "src/contexts/ThemeContext";
import { Button, ErrorAlert, InfoField } from "src/components";
import { formatNumberToLocale } from "src/utils";
import "./trustpayments-withdrawal-card-form.scss";

/** SecureTrading JS library — loaded from Trust's CDN on demand. */
const SECURE_TRADING_SDK_URL = "https://cdn.eu.trustpayments.com/js/latest/st.js";

interface SecureTradingResponseItem {
  errorcode?: string;
  errormessage?: string;
  errordata?: string[];
  transactionreference?: string;
}

interface SecureTradingResponse {
  payload?: { response?: SecureTradingResponseItem[] };
  response?: SecureTradingResponseItem[];
  errorcode?: string;
  errormessage?: string;
  errordata?: string[];
  transactionreference?: string;
}

interface SecureTradingInstance {
  Components: (config?: {
    callbacks?: {
      onPaymentFormRendered?: () => void;
      onPaymentFormValidityChange?: (data: { isFormValid?: boolean }) => void;
    };
  }) => void;
}

type StStyles = Record<string, string | boolean>;

interface SecureTradingFactory {
  (config: {
    jwt: string;
    livestatus: 0 | 1;
    styles?: StStyles;
    submitCallback?: (data: SecureTradingResponse) => void;
    errorCallback?: (data: SecureTradingResponse) => void;
    disableNotification?: boolean;
    submitOnSuccess?: boolean;
    submitOnError?: boolean;
    submitOnCancel?: boolean;
    formId?: string;
    buttonId?: string;
  }): SecureTradingInstance;
}

const getCssVariable = (name: string, fallback: string): string => {
  if (typeof window === "undefined") return fallback;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
};

const buildStStyles = (): StStyles => ({
  "background-color-body": "transparent",
  "space-inset-body": "0",
  "background-color-input": getCssVariable("--trustpayments-sdk-input-bg-color", "transparent"),
  "border-color-input": getCssVariable("--trustpayments-sdk-input-border-color", "rgba(0,0,0,0.12)"),
  "border-radius-input": "10px",
  "border-size-input": "1px",
  "color-input": getCssVariable("--trustpayments-sdk-input-text-color", "#1a1d2c"),
  "color-input-placeholder": getCssVariable("--trustpayments-sdk-input-placeholder-color", "rgba(0,0,0,0.35)"),
  "font-size-input": "15px",
  "space-inset-input": "12px 14px",
  "background-color-input:hover": getCssVariable("--trustpayments-sdk-input-bg-color", "transparent"),
  "border-color-input:hover": getCssVariable("--trustpayments-sdk-input-border-color", "rgba(0,0,0,0.12)"),
  "background-color-input:focus": getCssVariable("--trustpayments-sdk-input-bg-color", "transparent"),
  "outline-input": "none",
  "outline-input:focus": "none",
  "box-shadow-input": "none",
  "box-shadow-input:focus": getCssVariable("--trustpayments-sdk-input-focus-shadow", "0 0 0 2px rgba(91,233,168,0.16)"),
  "border-color-input:focus": getCssVariable("--trustpayments-sdk-input-border-color-focus", "#2cc7a8"),
  "background-color-input-error": getCssVariable("--trustpayments-sdk-input-error-bg-color", "rgba(235,80,72,0.06)"),
  "border-color-input-error": getCssVariable("--trustpayments-sdk-input-error-border-color", "#eb5048"),
  "color-input-error": getCssVariable("--trustpayments-sdk-input-error-text-color", "#c62828"),
  "color-label": getCssVariable("--trustpayments-sdk-label-color", "rgba(0,0,0,0.55)"),
  "font-size-label": "11px",
  "font-weight-label": "600",
  "text-transform-label": "uppercase",
  "display-asterisk": false,
  "color-error": getCssVariable("--trustpayments-sdk-message-error-color", "#c62828"),
  "font-size-message": "11.5px",
});

// `Window.SecureTrading` is already declared globally in
// `depositForm/TrustPaymentsCardForm.tsx`; redeclaring here would
// cause a "subsequent declaration" type conflict.
const stWindow = (): SecureTradingFactory | undefined =>
  (window as unknown as { SecureTrading?: SecureTradingFactory }).SecureTrading;

let sdkLoadPromise: Promise<void> | null = null;

const loadSecureTradingSdk = (): Promise<void> => {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (stWindow()) return Promise.resolve();
  if (sdkLoadPromise) return sdkLoadPromise;

  sdkLoadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SECURE_TRADING_SDK_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("SecureTrading SDK failed to load")));
      return;
    }
    const script = document.createElement("script");
    script.src = SECURE_TRADING_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("SecureTrading SDK failed to load"));
    document.head.appendChild(script);
  });

  return sdkLoadPromise;
};

// Pick out the first non-error response item that carries a tx ref.
// Trust's SDK can return either a single object or `payload.response[]`
// depending on the request type.
const findSuccessRef = (data: SecureTradingResponse): string | null => {
  const items = data?.payload?.response ?? data?.response ?? (data?.errorcode !== undefined ? [data] : []);
  for (const item of items) {
    if (item.errorcode === "0" && item.transactionreference) {
      return item.transactionreference;
    }
  }
  return null;
};

const findFirstError = (data: SecureTradingResponse): SecureTradingResponseItem | null => {
  const items = data?.payload?.response ?? data?.response ?? (data?.errorcode !== undefined ? [data] : []);
  return items.find((i) => i.errorcode && i.errorcode !== "0") ?? null;
};

interface Props {
  sessionId: string;
  onReset: () => void;
  targetAmount: number;
  sourceCurrency: string;
  targetCurrency: string;
  sourceCurrencyPrecision: number;
  targetCurrencyPrecision: number;
  estimatedReceive: number;
  submitWithdrawalWithRef: (parentTransactionReference: string) => Promise<void>;
  // When the parent has shown a terminal overlay (success / failed),
  // we keep the SDK iframes mounted but collapse the visible shell so
  // late SDK callbacks don't fall back to their own white loader.
  isTerminal: boolean;
  /** Locked FX quote — when present, "You will receive" headline
   *  switches to the user's local currency (BRL etc.). */
  fxQuote?: TrustPaymentsFxQuoteFx | null;
}

// SecureTrading SDK mount for the withdrawal step. JWT is signed for
// ACCOUNTCHECK on the BE — SDK collects card details, runs the
// zero-value check, returns a `transactionreference` we hand back to
// our BE which issues the real CFT REFUND.
const TrustPaymentsWithdrawalCardForm: React.FC<Props> = ({
  sessionId,
  onReset,
  targetAmount,
  sourceCurrency,
  targetCurrency,
  sourceCurrencyPrecision,
  targetCurrencyPrecision,
  estimatedReceive,
  submitWithdrawalWithRef,
  isTerminal,
  fxQuote,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [status, setStatus] = useState<"loading" | "ready" | "submitting">("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showCardValidationHint, setShowCardValidationHint] = useState(false);
  const initializedRef = useRef(false);
  const cardFormValidRef = useRef<boolean | null>(null);
  const cardSubmitAttemptedRef = useRef(false);
  // SDK fires multiple events per request; latch the first terminal
  // event so a late ACCOUNTCHECK info callback can't undo a successful
  // REFUND already in flight to our BE.
  const handledRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    let cancelled = false;

    const handleStResponse = async (data: SecureTradingResponse) => {
      if (handledRef.current) return;

      const firstError = findFirstError(data);
      if (firstError) {
        handledRef.current = true;
        const extra = (firstError.errordata || []).filter(Boolean).join(", ");
        const msg = firstError.errormessage || "Card validation failed";
        setErrorMessage(extra ? `${msg} — ${extra}` : msg);
        setShowCardValidationHint(false);
        // Drop the spinner so the user can see the error + retry. SDK
        // also returns to a re-armed state internally for the next
        // submit.
        setStatus("ready");
        return;
      }

      const ref = findSuccessRef(data);
      if (ref) {
        handledRef.current = true;
        setStatus("submitting");
        await submitWithdrawalWithRef(ref);
      }
    };

    const init = async () => {
      try {
        const [, session] = await Promise.all([loadSecureTradingSdk(), trustPaymentsService.getSession(sessionId)]);
        if (cancelled) return;
        const SecureTrading = stWindow();
        if (!SecureTrading) {
          throw new Error("SecureTrading SDK unavailable");
        }
        const st = SecureTrading({
          jwt: session.jwt,
          livestatus: 0,
          styles: buildStStyles(),
          submitCallback: handleStResponse,
          errorCallback: handleStResponse,
          disableNotification: true,
          submitOnSuccess: false,
          submitOnError: false,
          submitOnCancel: false,
          formId: "st-form",
          buttonId: "trustpayments-withdrawal-submit",
        });
        st.Components({
          callbacks: {
            onPaymentFormRendered: () => {
              if (!cancelled) setStatus("ready");
            },
            onPaymentFormValidityChange: (data) => {
              const isValid = !!data?.isFormValid;
              cardFormValidRef.current = isValid;
              setShowCardValidationHint(cardSubmitAttemptedRef.current && !isValid);
            },
          },
        });
      } catch (e) {
        if (cancelled) return;
        setErrorMessage(e instanceof Error ? e.message : String(e));
        setStatus("ready");
      }
    };

    init();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Reset the terminal-state guard when the user retries via "Try
  // again" so a second SDK submission can flow through.
  const handleRetry = () => {
    handledRef.current = false;
    setErrorMessage(null);
    onReset();
  };

  return (
    <section
      className={
        `trustpayments-withdrawal-card-form` +
        ` trustpayments-withdrawal-card-form--${theme === Theme.Dark ? "dark" : "light"}` +
        (isTerminal ? " trustpayments-withdrawal-card-form--hidden" : "")
      }
      aria-hidden={isTerminal}
    >
      <button
        type="button"
        className="trustpayments-withdrawal-card-form__back"
        onClick={handleRetry}
        disabled={status === "submitting"}
      >
        {t("TRUSTPAYMENTS_WITHDRAWAL.BACK", { defaultValue: "← Back" })}
      </button>

      <InfoField
        className="info-field--muted"
        label={t("TRUSTPAYMENTS_WITHDRAWAL.YOU_SELL", { defaultValue: "You sell" })}
        value={`${formatNumberToLocale(targetAmount, targetCurrencyPrecision)} ${targetCurrency}`}
      />

      {/* FX markup is baked into the local-currency amount, not shown
          as a separate line. */}
      <InfoField
        label={
          fxQuote
            ? t("TRUSTPAYMENTS_WITHDRAWAL.YOU_RECEIVE_LOCAL", {
                currency: fxQuote.local_currency,
                defaultValue: `You will receive in ${fxQuote.local_currency}`,
              })
            : t("TRUSTPAYMENTS_WITHDRAWAL.YOU_RECEIVE", {
                defaultValue: "You will receive",
              })
        }
        value={
          fxQuote
            ? `${formatNumberToLocale(fxQuote.local_amount, 2)} ${fxQuote.local_currency}`
            : `${formatNumberToLocale(estimatedReceive, sourceCurrencyPrecision)} ${sourceCurrency}`
        }
      />

      <form
        id="st-form"
        className="trustpayments-withdrawal-card-form__form"
        aria-busy={status === "loading"}
        noValidate
        onSubmit={(e) => {
          if (status !== "ready") {
            e.preventDefault();
            return;
          }
          // SDK intercepts and processes asynchronously — flip to
          // submitting immediately so the user sees the button as
          // pending instead of "nothing happens". handleStResponse
          // resets to "ready" on error or to terminal on success.
          cardSubmitAttemptedRef.current = true;
          setShowCardValidationHint(cardFormValidRef.current === false);
          if (cardFormValidRef.current === false) {
            e.preventDefault();
            return;
          }
          setStatus("submitting");
        }}
      >
        <div
          className={
            "trustpayments-withdrawal-card-form__sdk-loader" +
            (status !== "loading" ? " trustpayments-withdrawal-card-form__sdk-loader--hidden" : "")
          }
          role="status"
          aria-hidden={status !== "loading"}
        >
          <span className="trustpayments-withdrawal-card-form__sdk-spinner" aria-hidden />
          <span>
            {t("TRUSTPAYMENTS_WITHDRAWAL.LOADING_CARD_FORM", {
              defaultValue: "Loading secure card form…",
            })}
          </span>
        </div>

        <div id="st-card-number" />
        <div className="trustpayments-withdrawal-card-form__row">
          <div id="st-expiration-date" />
          <div id="st-security-code" />
        </div>

        {showCardValidationHint ? (
          <p className="trustpayments-withdrawal-card-form__field-error">
            {t("TRUSTPAYMENTS_WITHDRAWAL.CARD_VALIDATION_HINT", {
              defaultValue: "Please fill in the highlighted card fields.",
            })}
          </p>
        ) : (
          <p className="trustpayments-withdrawal-card-form__hint">
            {t("TRUSTPAYMENTS_WITHDRAWAL.CARD_HINT", {
              defaultValue: "Enter the card you want to receive funds on. We'll verify it, then send your payout.",
            })}
          </p>
        )}

        <Button
          id="trustpayments-withdrawal-submit"
          title={t("TRUSTPAYMENTS_WITHDRAWAL.CONFIRM_PAYOUT", {
            defaultValue: "Confirm withdrawal",
          })}
          className="deposit-button"
          type="submit"
          disabled={status !== "ready"}
          pending={status === "loading" || status === "submitting"}
          onClick={() => {
            cardSubmitAttemptedRef.current = true;
            setShowCardValidationHint(cardFormValidRef.current === false);
          }}
        />
      </form>

      <ErrorAlert message={errorMessage} />
    </section>
  );
};

export default TrustPaymentsWithdrawalCardForm;
